import { ref, watch, onUnmounted } from 'vue';

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchmove',
  'touchstart',
  'wheel',
];

/**
 * Persisted (not in-memory) last-activity timestamp. The in-memory `setTimeout` below only
 * detects idleness while the page itself keeps running — it does NOT catch a closed tab,
 * browser relaunch, or an OS sleep/hibernate across a multi-day gap (e.g. a laptop locked over
 * a weekend). On the next page load, the app would otherwise silently resume the session via
 * the persisted auth state + httpOnly refresh cookie with no idea how long it's been idle.
 * Persisting this timestamp lets `hasIdleTimeoutElapsed()` be checked once at boot, before that
 * silent resume happens.
 */
const LAST_ACTIVITY_STORAGE_KEY = 'tms-last-activity-at';
/** Avoids writing to localStorage on every single mousemove tick. */
const ACTIVITY_PERSIST_THROTTLE_MS = 5000;

let lastPersistedActivityAt = 0;

function persistLastActivity(now: number): void {
  if (now - lastPersistedActivityAt < ACTIVITY_PERSIST_THROTTLE_MS) return;
  lastPersistedActivityAt = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable (private mode quota, etc.) — in-memory idle detection still works
  }
}

/**
 * Whether more than `timeoutMs` has elapsed since the last recorded user activity, per the
 * timestamp persisted above. Fails CLOSED (returns `true`, i.e. "treat as idle") whenever no
 * usable timestamp is found — including the rollout gap where a session persisted by a
 * pre-this-feature app version has auth identity but was never given a chance to record one.
 * Callers only invoke this when persisted auth state already exists, so an absent/corrupt
 * timestamp in that context must force a fresh login rather than silently resuming.
 *
 * Call this at app boot, before silently re-establishing a session from persisted auth state /
 * the httpOnly refresh cookie, so a real-world gap (closed tab, OS sleep/hibernate over a
 * weekend) forces a fresh login instead of resuming as if no time had passed.
 */
export function hasIdleTimeoutElapsed(timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS): boolean {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    return true;
  }
  if (!raw) return true;
  const lastActivityAt = Number(raw);
  if (!Number.isFinite(lastActivityAt)) return true;
  return Date.now() - lastActivityAt > timeoutMs;
}

/** Clears the persisted last-activity timestamp — call on logout so the next login isn't immediately flagged idle. */
export function clearLastActivityTimestamp(): void {
  lastPersistedActivityAt = 0;
  try {
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear
  }
}

/**
 * Triggers onIdle callback after the user has been inactive for the given timeout.
 * Activity events tracked: mousemove, mousedown, keydown, touchmove, touchstart, wheel.
 *
 * @param onIdle          - Callback invoked when the idle timeout is reached.
 * @param options.timeout - Inactivity duration in ms before onIdle fires (default: 15 minutes).
 * @param options.active  - Getter that returns whether the timer should be running.
 *                          When it returns false the timer is stopped (e.g. on the login page).
 *                          When omitted the timer always runs.
 */
export function useIdleTimeout(
  onIdle: () => void | Promise<void>,
  options: { timeout?: number; active?: () => boolean } = {},
): void {
  const { timeout = DEFAULT_IDLE_TIMEOUT_MS, active } = options;

  const idle = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  function reset(): void {
    idle.value = false;
    clearTimeout(timer);
    persistLastActivity(Date.now());
    timer = setTimeout(() => {
      idle.value = true;
    }, timeout);
  }

  function stopListening(): void {
    clearTimeout(timer);
    timer = undefined;
    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, reset);
    }
  }

  function startListening(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, reset, { passive: true });
    }
    reset();
  }

  if (active) {
    // Watch the getter reactively so the timer starts/stops with auth state
    watch(active, (isActive) => {
      if (isActive) {
        startListening();
      } else {
        stopListening();
      }
    }, { immediate: true });
  } else {
    startListening();
  }

  onUnmounted(() => {
    stopListening();
  });

  let idleRunning = false;
  watch(idle, async (isIdle) => {
    if (isIdle && !idleRunning) {
      idleRunning = true;
      try {
        await onIdle();
      } finally {
        idleRunning = false;
      }
    }
  });
}
